<script setup lang="ts">
import { ref, computed, watch, onErrorCaptured, type ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import Button from '@/components/ui/Button.vue'
import { loggerService } from '@/services/logger.service'

interface Props {
  fallback?: string
}

const props = defineProps<Props>()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const error = ref<Error | null>(null)
const errorInfo = ref<string>('')
const isDev = import.meta.env.DEV

const errorTitle = computed(() => {
  try {
    const txt = typeof t === 'function' ? t('errors.unexpectedError') : null
    return txt ?? 'An unexpected error occurred'
  } catch {
    return 'An unexpected error occurred'
  }
})

const retryLabel = computed(() => {
  try {
    const txt = typeof t === 'function' ? t('common.retry') : null
    return txt ?? 'Retry'
  } catch {
    return 'Retry'
  }
})

const backToHomeLabel = computed(() => {
  try {
    const txt = typeof t === 'function' ? t('common.backToHome') : null
    return txt ?? 'Back to Home'
  } catch {
    return 'Back to Home'
  }
})

const fallbackText = computed(() => {
  if (props.fallback) return props.fallback
  try {
    return t('errors.boundaryBody')
  } catch {
    return 'Something went wrong. Please try again.'
  }
})

onErrorCaptured((err: Error, _instance: ComponentPublicInstance | null, info: string) => {
  error.value = err
  errorInfo.value = info

  loggerService.logError(
    err instanceof Error ? err : new Error(String(err)),
    { action: 'error_boundary', route: window.location.pathname }
  )

  return false // Prevent error from propagating
})

function clearError() {
  error.value = null
  errorInfo.value = ''
}

watch(() => route.fullPath, clearError)

const goHome = () => {
  clearError()
  void router.push('/')
}

const retry = () => {
  clearError()
  window.location.reload()
}
</script>

<template>
  <div
    v-if="error"
    class="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900"
  >
    <div class="card max-w-md w-full p-8 text-center shadow-lg">
      <div class="mb-6">
        <svg
          class="mx-auto h-16 w-16 text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      
      <h1 class="text-2xl font-bold text-body mb-4">
        {{ errorTitle }}
      </h1>
      
      <p class="text-gray-600 dark:text-gray-400 mb-6">
        {{ fallbackText }}
      </p>
      
      <div
        v-if="isDev"
        class="mb-6 p-4 bg-gray-100 dark:bg-gray-700/50 rounded text-start text-sm"
      >
        <p class="font-semibold mb-2 text-body">
          Error Details (Dev Only):
        </p>
        <p class="text-red-600 dark:text-red-400 mb-2">
          {{ error?.message }}
        </p>
        <p class="text-gray-600 dark:text-gray-400">
          {{ errorInfo }}
        </p>
      </div>
      
      <div class="flex gap-4 justify-center">
        <Button
          variant="primary"
          @click="retry"
        >
          {{ retryLabel }}
        </Button>
        <Button
          variant="secondary"
          @click="goHome"
        >
          {{ backToHomeLabel }}
        </Button>
      </div>
    </div>
  </div>
  
  <slot v-else />
</template>
